import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { PushSubscriptionService } from './push-subscription.service';

/** Browser/push categories - the ones `sendNotification` gates on. */
export type BrowserNotificationCategory = 'threadShared' | 'threadAccessRemoved' | 'projectShared' | 'projectAccessRemoved';

/** Email categories. Consumed server-side when a share or revocation happens;
 *  nothing in the client reads them. They share this record because they share
 *  one table and one endpoint with the browser categories. */
export type EmailNotificationCategory =
    | 'emailSharingEnabled'
    | 'emailThreadShared'
    | 'emailProjectShared'
    | 'emailThreadAccessRemoved'
    | 'emailProjectAccessRemoved'
    | 'emailInternalShares';

export type NotificationCategory = BrowserNotificationCategory | EmailNotificationCategory;

export type NotificationPreferences = Record<NotificationCategory, boolean>;

const STORAGE_KEY = 'ciq_notification_prefs';

/** Shared browser and email notification categories must stay in lockstep
 *  with NOTIFICATION_CATEGORY_DEFAULTS in backend/data_layer_push.py: that dict
 *  is also the write allowlist, so a category present here and missing there is
 *  silently dropped on save (backend also defines mobile-only categories like
 *  ddrMessages). */
const DEFAULTS: NotificationPreferences = {
    threadShared: true,
    threadAccessRemoved: true,
    projectShared: true,
    projectAccessRemoved: true,
    // Master gate for every sharing email.
    emailSharingEnabled: true,
    emailThreadShared: true,
    emailProjectShared: true,
    // Revocations are opt-in: losing access is the case most likely to read as
    // inbox noise.
    emailThreadAccessRemoved: false,
    emailProjectAccessRemoved: false,
    // Off is what lets this ship without a behaviour change. Before it, only
    // invite-by-email sent mail and those go to people outside the org; shares
    // from colleagues were silent, so emailing them has to be opted into.
    emailInternalShares: false,
};

@Injectable({
    providedIn: 'root',
})
export class BrowserNotificationService {
    private readonly http = inject(HttpClient);
    private readonly pushService = inject(PushSubscriptionService);

    readonly permission = signal<NotificationPermission>(this.currentPermission());
    readonly preferences = signal<NotificationPreferences>(this.loadLocalPreferences());

    async loadPreferencesFromApi(): Promise<void> {
        try {
            const response = await firstValueFrom(
                this.http.get<{ preferences: NotificationPreferences }>(`${environment.apiBase}/user/notification-preferences`)
            );
            this.preferences.set({ ...DEFAULTS, ...response.preferences });
            this.migrateLocalPreferencesToApi();
        } catch {
            // API unavailable - keep local preferences
        }
    }

    async sendNotification(category: BrowserNotificationCategory, title: string, options?: NotificationOptions & { autoCloseMs?: number }) {
        if (!this.preferences()[category]) {
            return;
        }

        // When push is subscribed, the SW handles notifications via server push.
        // Skip client-side to avoid duplicates.
        if (this.pushService.isSubscribed()) {
            return;
        }

        const permission = await this.requestPermissionIfNeeded();

        if (permission !== 'granted') {
            return;
        }

        const { autoCloseMs, ...notificationOptions } = options ?? {};

        const swReg = await this.getServiceWorkerRegistration();
        if (swReg) {
            await swReg.showNotification(title, notificationOptions);
            if (autoCloseMs) {
                setTimeout(async () => {
                    const notifications = await swReg.getNotifications({ tag: notificationOptions.tag });
                    notifications.forEach(n => n.close());
                }, autoCloseMs);
            }
        } else {
            try {
                const notification = new Notification(title, notificationOptions);
                if (autoCloseMs) {
                    setTimeout(() => notification.close(), autoCloseMs);
                }
            } catch {
                // new Notification() throws on iOS Safari; SW path handles mobile
            }
        }
    }

    async requestPermission(): Promise<NotificationPermission> {
        if (!('Notification' in window)) {
            return 'denied';
        }
        const result = await Notification.requestPermission();
        this.permission.set(result);
        return result;
    }

    async savePreferences(prefs: NotificationPreferences): Promise<void> {
        this.preferences.set(prefs);
        try {
            await firstValueFrom(this.http.put(`${environment.apiBase}/user/notification-preferences`, prefs));
        } catch {
            // Fallback: save locally if API fails
            localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
        }
    }

    refreshPermission() {
        this.permission.set(this.currentPermission());
    }

    private loadLocalPreferences(): NotificationPreferences {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                return { ...DEFAULTS, ...JSON.parse(stored) };
            } catch {
                return { ...DEFAULTS };
            }
        }
        return { ...DEFAULTS };
    }

    private async migrateLocalPreferencesToApi(): Promise<void> {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return;
        }
        try {
            const local = JSON.parse(stored) as Partial<NotificationPreferences>;
            await firstValueFrom(this.http.put(`${environment.apiBase}/user/notification-preferences`, local));
            localStorage.removeItem(STORAGE_KEY);
        } catch {
            // Migration failed - will retry next load
        }
    }

    private currentPermission(): NotificationPermission {
        if (!('Notification' in window)) {
            return 'denied';
        }
        return Notification.permission;
    }

    private async requestPermissionIfNeeded(): Promise<NotificationPermission> {
        if (Notification.permission === 'default') {
            const result = await Notification.requestPermission();
            this.permission.set(result);
            return result;
        }

        return Notification.permission;
    }

    private async getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | null> {
        if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) {
            return null;
        }
        try {
            return await navigator.serviceWorker.ready;
        } catch {
            return null;
        }
    }
}
